import { lazy } from 'react';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import Routes from './RouteNames';

// pages routing
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const HomePage = Loadable(lazy(() => import('pages/home/home-page')));
const InvoiceDetailPage = Loadable(lazy(() => import('pages/invoice-details/invoice-details-page')));
const OrdersPage = Loadable(lazy(() => import('pages/orders/orders-page')));
const OrderQuotesPage = Loadable(lazy(() => import('pages/orders/order-quotes-page')));
const SalesOrderQuoteDetailPage = Loadable(lazy(() => import('pages/orders/sales-order-quotes/sales-order-quote-details-page')));
const DeliveriesPage = Loadable(lazy(() => import('pages/deliveries/deliveries-page')));
const DeliveryDetailsPage = Loadable(lazy(() => import('pages/deliveries/delivery-details-page')));
const ReturnsPage = Loadable(lazy(() => import('pages/returns/returns-page')));
const ReturnDetailsPage = Loadable(lazy(() => import('pages/returns/returns-details-page')));
const ProductStocksPage = Loadable(lazy(() => import('pages/stocks/product-list-page')));
const FeedsPage = Loadable(lazy(() => import('pages/feeds/feeds-page')));
const InboundIntegrationsPage = Loadable(lazy(() => import('pages/inbound-integrations/inbound-integrations-page')));
const OutboundIntegrationsPage = Loadable(lazy(() => import('pages/outbound-integrations/outbound-integrations-page')));
const IntegrationEventsPage = Loadable(lazy(() => import('pages/integration-events/integration-events-page')));
const AccountAdminPage = Loadable(lazy(() => import('pages/account-admin/account-page')));
const ProductPromotionsPage = Loadable(lazy(() => import('pages/sales-promotions/sales-promotions')));
const SalesPromotionDetailsPage = Loadable(lazy(() => import('pages/sales-promotions/sales-promotion-details')));
const SalesPromotionClaimPage = Loadable(lazy(() => import('pages/sales-promotions/sales-promotion-claim-page')));
const ReportsPage = Loadable(lazy(() => import('pages/reports/reports-page')));
const ReportPage = Loadable(lazy(() => import('pages/reports/report-page')));
const TeamAdminPage = Loadable(lazy(() => import('pages/team-admin/team-admin-page')));
const SalesOrderDetailsPage = Loadable(lazy(() => import('pages/orders/sales-order-details-page')));
const MarketingPage = Loadable(lazy(() => import('pages/marketing/marketing-page')));
const MarketingDetailsPage = Loadable(lazy(() => import('pages/marketing/market-details-page')));
const ProductDetailsPage = Loadable(lazy(() => import('pages/stocks/product-details-page')));
const CreateOrderPage = Loadable(lazy(() => import('pages/orders/order-create-page')));
const OrderDraftsPage = Loadable(lazy(() => import('pages/orders/order-drafts-page')));
const InvoicesPage = Loadable(lazy(() => import('pages/invoices/invoices-page')));
const MarketingDetailsEditPage = Loadable(lazy(() => import('pages/marketing/marketing-details-edit-page')));
const OrderImportPage = Loadable(lazy(() => import('pages/orders/order-import-page')));
const RestApiKeysPage = Loadable(lazy(() => import('pages/inbound-integrations/rest-api-keys-page')));
const FileApiDetailsPage = Loadable(lazy(() => import('pages/inbound-integrations/file-api-details-page')));
const AdvertisingBannerConfigurationsPage = Loadable(lazy(() => import('pages/advertising/advertising-banner-configurations-page')));
const AdvertisingBannerConfigurationEditPage = Loadable(
  lazy(() => import('pages/advertising/sales-advertising-banner-configuration-edit-page'))
);

// ==============================|| MAIN ROUTING ||============================== //

export interface RouteMetaData {
  pageGroupIdentifier?: string;
  isFilterPage?: boolean;
}

const MainRoutes = (basePath: string) => {
  return {
    path: '/',
    children: [
      {
        path: '/',
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: `${basePath}${Routes.Home}`,
            element: <HomePage />,
            meta: { pageGroupIdentifier: 'Home' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Invoices}`,
            element: <InvoicesPage />,
            meta: { pageGroupIdentifier: 'Invoices', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Invoices}/:id`,
            element: <InvoiceDetailPage />,
            meta: { pageGroupIdentifier: 'Invoices' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Team}`,
            element: <TeamAdminPage />,
            meta: { pageGroupIdentifier: 'Team-admin' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Orders}`,
            element: <OrdersPage />,
            meta: { pageGroupIdentifier: 'Orders', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.OrderQuotes}`,
            element: <OrderQuotesPage />,
            meta: { pageGroupIdentifier: 'Quotes', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.OrderQuotes}/:id`,
            element: <SalesOrderQuoteDetailPage />,
            meta: { pageGroupIdentifier: 'Quotes' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.OrderImport}`,
            element: <OrderImportPage />,
            meta: { pageGroupIdentifier: 'Orders' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Orders}/:id`,
            element: <SalesOrderDetailsPage />,
            meta: { pageGroupIdentifier: 'Orders' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.OrderDrafts}`,
            element: <OrderDraftsPage />,
            meta: { pageGroupIdentifier: 'Orders' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.OrderDrafts}/:id`,
            element: <CreateOrderPage />,
            meta: { pageGroupIdentifier: 'Orders' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.CreateOrder}`,
            element: <CreateOrderPage />,
            meta: { pageGroupIdentifier: 'Orders' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Deliveries}`,
            element: <DeliveriesPage />,
            meta: { pageGroupIdentifier: 'Deliveries', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Deliveries}/:id`,
            element: <DeliveryDetailsPage />,
            meta: { pageGroupIdentifier: 'Deliveries' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Returns}`,
            element: <ReturnsPage />,
            meta: { pageGroupIdentifier: 'Returns', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Returns}/:id`,
            element: <ReturnDetailsPage />,
            meta: { pageGroupIdentifier: 'Returns' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Products}`,
            element: <ProductStocksPage />,
            meta: { pageGroupIdentifier: 'Products', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Products}/:id`,
            element: <ProductDetailsPage />,
            meta: { pageGroupIdentifier: 'Products' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Feeds}`,
            element: <FeedsPage />
          },
          {
            path: `${basePath}${Routes.RestApiDocs}`,
            element: <FeedsPage />
          },
          {
            path: `${basePath}${Routes.RestApiKeys}`,
            element: <RestApiKeysPage />
          },
          {
            path: `${basePath}${Routes.FileSftpDetails}`,
            element: <FileApiDetailsPage />
          },
          {
            path: `${basePath}${Routes.InboundIntegrations}`,
            element: <InboundIntegrationsPage />
          },
          {
            path: `${basePath}${Routes.OutboundIntegrations}`,
            element: <OutboundIntegrationsPage />
          },
          {
            path: `${basePath}${Routes.IntegrationEvents}`,
            element: <IntegrationEventsPage />
          },
          {
            path: `${basePath}${Routes.Account}`,
            element: <AccountAdminPage />,
            meta: { pageGroupIdentifier: 'Account' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Promotions}`,
            element: <ProductPromotionsPage />,
            meta: { pageGroupIdentifier: 'Promotions', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Promotions}/:id`,
            element: <SalesPromotionDetailsPage />,
            meta: { pageGroupIdentifier: 'Promotions' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Promotions}/:id/claim`,
            element: <SalesPromotionClaimPage />,
            meta: { pageGroupIdentifier: 'Promotions' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Reports}`,
            element: <ReportsPage />,
            meta: { pageGroupIdentifier: 'Reports', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Reports}/:id`,
            element: <ReportPage />,
            meta: { pageGroupIdentifier: 'Reports' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Marketing}`,
            element: <MarketingPage />,
            meta: { pageGroupIdentifier: 'Marketing', IsFilterPage: true } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Marketing}/create`,
            element: <MarketingDetailsEditPage />,
            meta: { pageGroupIdentifier: 'Marketing' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Marketing}/edit/:id`,
            element: <MarketingDetailsEditPage />,
            meta: { pageGroupIdentifier: 'Marketing' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.Marketing}/:id`,
            element: <MarketingDetailsPage />,
            meta: { pageGroupIdentifier: 'Marketing' } as RouteMetaData
          },
          {
            path: `${basePath}${Routes.AdvertisingConfiguration}`,
            element: <AdvertisingBannerConfigurationsPage />
          },
          {
            path: `${basePath}${Routes.AdvertisingConfiguration}/create`,
            element: <AdvertisingBannerConfigurationEditPage />
          },
          {
            path: `${basePath}${Routes.AdvertisingConfiguration}/:id`,
            element: <AdvertisingBannerConfigurationEditPage />
          },
          {
            path: '*',
            element: <HomePage />
          }
        ]
      },
      {
        path: '/maintenance',
        element: <MainLayout />,
        children: [
          {
            path: '404',
            element: <MaintenanceError />
          },
          {
            path: '500',
            element: <MaintenanceError500 />
          }
        ]
      }
    ]
  };
};

export default MainRoutes;
